/**
 * Stages API services
 *
 * Task stages are organizational groupings for the Kanban board.
 * Unlike status, stages can be customized by the user.
 */

import apiClient from './client';

export interface Stage {
  id: string;
  name: string;
  sequence: number;
  isCollapsed: boolean;
  isClosed: boolean;
  createdAt: string;
  project?: {
    id: string;
    name: string;
  } | null;
  taskCount: number;
}

export interface StageCreate {
  name: string;
  projectId?: string;
  sequence?: number;
  isCollapsed?: boolean;
  isClosed?: boolean;
}

export interface StageUpdate {
  name?: string;
  sequence?: number;
  isCollapsed?: boolean;
  isClosed?: boolean;
}

const transformStage = (data: any): Stage => ({
  id: data.id,
  name: data.name,
  sequence: data.sequence,
  isCollapsed: data.is_collapsed ?? false,
  isClosed: data.is_closed ?? false,
  createdAt: data.created_at,
  project: data.project,
  taskCount: data.task_count ?? 0,
});

export const stagesApi = {
  /**
   * List all stages
   */
  list: async (params?: { projectId?: string }): Promise<Stage[]> => {
    const queryParams: any = {};
    if (params?.projectId) queryParams.project_id = params.projectId;

    const response = await apiClient.get('/v1/stages', { params: queryParams });
    const items = response.data.items || response.data || [];
    return items.map(transformStage);
  },

  /**
   * Get a single stage
   */
  get: async (id: string): Promise<Stage> => {
    const response = await apiClient.get(`/v1/stages/${id}`);
    return transformStage(response.data);
  },

  /**
   * Create a new stage
   */
  create: async (data: StageCreate): Promise<Stage> => {
    const payload: any = {
      name: data.name,
    };
    if (data.projectId) payload.project_id = data.projectId;
    if (data.sequence !== undefined) payload.sequence = data.sequence;
    if (data.isCollapsed !== undefined) payload.is_collapsed = data.isCollapsed;
    if (data.isClosed !== undefined) payload.is_closed = data.isClosed;

    const response = await apiClient.post('/v1/stages', payload);
    return transformStage(response.data);
  },

  /**
   * Update a stage
   */
  update: async (id: string, data: StageUpdate): Promise<Stage> => {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.sequence !== undefined) payload.sequence = data.sequence;
    if (data.isCollapsed !== undefined) payload.is_collapsed = data.isCollapsed;
    if (data.isClosed !== undefined) payload.is_closed = data.isClosed;

    const response = await apiClient.put(`/v1/stages/${id}`, payload);
    return transformStage(response.data);
  },

  /**
   * Delete a stage
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/stages/${id}`);
  },

  /**
   * Reorder stages
   */
  reorder: async (stageIds: string[]): Promise<void> => {
    await apiClient.post('/v1/stages/reorder', { stage_ids: stageIds });
  },

  /**
   * Move a task to a different stage
   */
  moveTask: async (taskId: string, stageId: string): Promise<void> => {
    await apiClient.patch(`/v1/instances/${taskId}`, { stage_id: stageId });
  },
};
